if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        // Try to initialize immediately if DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            this.tryInit();
          });
        } else {
          // DOM is already ready, try to initialize
          setTimeout(() => this.tryInit(), 100);
        }
      }

      tryInit() {
        // Check if element is already in viewport or use inView callback
        if (typeof FoxTheme !== 'undefined' && FoxTheme.Motion && FoxTheme.Motion.inView) {
          const wrapper = this.closest('.countdown__wrapper');
          if (wrapper) {
            // Check if element is already visible
            const rect = wrapper.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight + 200 && rect.bottom > -200;
            
            if (isVisible) {
              // Already visible, initialize immediately
              this.init();
            } else {
              // Not visible yet, wait for inView
              FoxTheme.Motion.inView(wrapper, this.init.bind(this), {
                margin: '200px 0px 0px 0px',
              });
            }
          } else {
            // No wrapper found, initialize immediately
            this.init();
          }
        } else {
          // FoxTheme not available, initialize immediately
          this.init();
        }
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.loop = false;
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time':
            const countdownData = this.dataset.dueDate;
            if (countdownData) {
              this.targetTime = this.parseFixedTime(countdownData);
              if (!this.targetTime) {
                console.warn('Countdown timer: Failed to parse fixed time:', countdownData);
                return;
              }
            } else {
              console.warn('Countdown timer: No due date provided for fixed_time type');
              return;
            }
            break;
          default: // Evergreen.
            this.targetTime = this.getEvergreenDueDate().getTime();
            break;
        }

        if (!this.targetTime) {
          console.warn('Countdown timer: No target time set');
          return;
        }

        this.startTime = Date.now();
        
        if (this.targetTime > this.startTime) {
          this.fetchDomElements();
          this.beginCountdown();
        } else {
          // Target time is in the past - show timer at 00:00:00:00
          this.fetchDomElements();
          this.updateDisplay(0);
          this.classList.remove('hidden');
        }
      }

      parseFixedTime(dateString) {
        if (!dateString || dateString.trim() === '') {
          console.warn('Countdown timer: Empty date string');
          return null;
        }

        // Trim and normalize the date string
        dateString = dateString.trim();

        // Try to parse the date string
        // Format expected: "2025/08/20 03:45 PM" or similar variations
        let parsedDate = new Date(dateString);
        
        // Check if the date is valid
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getTime();
        }

        // If direct parsing fails, try to parse the format "YYYY/MM/DD HH:MM AM/PM"
        // This regex handles formats like "2025/08/20 03:45 PM" or "2025/8/20 3:45 PM"
        const dateMatch = dateString.match(/(\d{4})\/(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (dateMatch) {
          const year = parseInt(dateMatch[1], 10);
          const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
          const day = parseInt(dateMatch[3], 10);
          let hour = parseInt(dateMatch[4], 10);
          const minute = parseInt(dateMatch[5], 10);
          const ampm = dateMatch[6].toUpperCase();

          // Convert to 24-hour format
          if (ampm === 'PM' && hour !== 12) {
            hour += 12;
          } else if (ampm === 'AM' && hour === 12) {
            hour = 0;
          }

          parsedDate = new Date(year, month, day, hour, minute, 0, 0);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.getTime();
          }
        }

        // Try format without AM/PM (24-hour format): "2025/08/20 15:45" or "2025/11/11 12:58"
        // This regex matches: YYYY/MM/DD HH:MM (with optional leading zeros, flexible whitespace)
        const dateMatch24 = dateString.match(/(\d{4})\/(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})/);
        if (dateMatch24) {
          const year = parseInt(dateMatch24[1], 10);
          const month = parseInt(dateMatch24[2], 10) - 1; // Month is 0-indexed
          const day = parseInt(dateMatch24[3], 10);
          const hour = parseInt(dateMatch24[4], 10);
          const minute = parseInt(dateMatch24[5], 10);
          
          parsedDate = new Date(year, month, day, hour, minute, 0, 0);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.getTime();
          }
        }

        // Last resort: try ISO format or other common formats
        parsedDate = new Date(dateString.replace(/\//g, '-'));
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getTime();
        }

        console.warn('Countdown timer: Unable to parse date:', dateString);
        return null;
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        // Ensure DOM elements are fetched
        if (!this.elements || !this.elements.day) {
          this.fetchDomElements();
        }
        
        // Check if elements were found
        if (!this.elements || !this.elements.day) {
          console.error('Countdown timer: DOM elements not found!');
          return;
        }
        
        // Update display immediately
        const initialRemainingTime = this.targetTime - Date.now();
        if (initialRemainingTime > 0) {
          this.updateDisplay(initialRemainingTime);
        } else {
          // Time has already passed
          this.updateDisplay(0);
        }
        
        // Remove hidden class to show the timer
        this.classList.remove('hidden');
        
        // Start the countdown interval
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();
          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);
      }

      updateDisplay(remainingTime) {
        if (!this.elements) {
          console.error('Countdown timer: Cannot update display - elements not found');
          return;
        }
        
        const timeComponents = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);
        if (this.loop) {
          this.beginCountdown();
        } else {
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = '00';
          }
        });
      }

      getEvergreenDueDate() {
        let intervalHrs;
        const now = new Date();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
            dueDate.setDate(1); // Start from the 1st day of the next month.
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week':
            const dayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - dayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return dueDate;
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }
    }
  );
}
